/**
 * StolenCardToast.tsx — WEB.4.UI.12
 *
 * Transient notification shown after a StealCard@OpponentChosen play
 * (Spy_In_Action / Policy_Infiltration / Policy_Exchange). Auto-dismisses
 * after 4 seconds. Click to dismiss immediately.
 */

import type React from "react";
import { useEffect } from "react";
import { useI18n } from "../../i18n/useI18n.js";
import { getCardById, useGameStore } from "../../store/game-store.js";
import type { SeatIndex } from "../../store/game-store.js";
import styles from "./StolenCardToast.module.css";

const SEAT_NAMES = ["Player 1", "Player 2", "Player 3", "Player 4"] as const;

function seatName(seat: SeatIndex): string {
  return SEAT_NAMES[seat] ?? `P${seat + 1}`;
}

export function StolenCardToast(): React.ReactElement | null {
  const { t } = useI18n();
  const lastStolenCard = useGameStore((s) => s.lastStolenCard);
  const dismissStolenCardToast = useGameStore((s) => s.dismissStolenCardToast);

  useEffect(() => {
    if (lastStolenCard === null) return;
    const timer = window.setTimeout(() => {
      dismissStolenCardToast();
    }, 4000);
    return () => window.clearTimeout(timer);
  }, [lastStolenCard, dismissStolenCardToast]);

  if (lastStolenCard === null) return null;

  const { from, to, cardId } = lastStolenCard;
  const cardDef = getCardById(cardId);
  const cardDisplay = cardDef?.cardName ?? cardId;

  return (
    <button
      type="button"
      className={styles.toast}
      onClick={dismissStolenCardToast}
      aria-label="Dismiss stolen card notification"
      data-testid="stolen-card-toast"
      data-from-seat={from}
      data-to-seat={to}
      data-card-id={cardId}
    >
      <div className={styles.title}>{t("toast.stolenCard.title")}</div>
      <div className={styles.body}>
        <span className={styles.thief}>{seatName(to)}</span>
        <span className={styles.arrow}>{t("toast.stolenCard.arrow")}</span>
        <span className={styles.victim}>{seatName(from)}</span>
      </div>
      <div className={styles.cardName}>{cardDisplay}</div>
    </button>
  );
}
